import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { type FC, useState } from 'react';
import type { PromptSectionDraft } from 'services/types/promptSection';
import {
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

interface Props {
  close: () => void;
  promptSection: PromptSectionDraft;
  save?: (promptSection: PromptSectionDraft) => void;
}

const SimplePromptSectionModal: FC<Props> = ({ close, promptSection, save }) => {
  const [name, setName] = useState<string>(promptSection.name);
  const [displayName, setDisplayName] = useState<string>(promptSection.displayName);
  const [value, setValue] = useState<string>(promptSection.value);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value.replace(/[^a-zA-Z0-9]/g, '_'));
  };

  const handleSave = () => {
    if (save) {
      save({
        ...promptSection,
        name,
        displayName,
        value,
        permission: 'editable',
      });
    }
    close();
  };

  const isFormValid = name.trim() && displayName.trim() && value.trim();

  return (
    <Modal open onClose={close}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92%', sm: '80%', md: '60%', lg: '50%' },
          maxHeight: '90vh',
          overflow: 'auto',
          borderRadius: RADIUS_CARD,
          bgcolor: SURFACE_CONTAINER_LOWEST,
          backdropFilter: GLASS_BLUR,
          boxShadow: SHADOW,
        }}
      >
        <Stack spacing={3} sx={{ p: { xs: 3, md: 4 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: RADIUS_ITEM,
                bgcolor: NAVY,
                color: '#fff',
                flexShrink: 0,
              }}
            >
              <AddIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Jost, sans-serif' }}>
                Agregar Nueva Seccion
              </Typography>
              <Typography variant="body2" sx={{ color: NAVY, opacity: 0.5, fontFamily: 'Jost, sans-serif' }}>
                Personaliza el conocimiento de tu agente
              </Typography>
            </Box>
          </Box>

          {/* Form */}
          <Stack spacing={2.5}>
            <TextField
              value={name}
              onChange={handleNameChange}
              label="Título"
              placeholder="Ej: politicas_devolucion, horarios_atencion"
              fullWidth
              disabled={!!promptSection.name}
              helperText="Solo letras, numeros y guiones bajos. No se puede cambiar despues."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: RADIUS_ITEM,
                  bgcolor: SURFACE_CONTAINER_LOW,
                  fontFamily: 'Jost, sans-serif',
                  '& fieldset': { borderColor: 'transparent' },
                  '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: '1.5px' },
                },
                '& .MuiInputLabel-root': { fontFamily: 'Jost, sans-serif' },
              }}
            />

            <TextField
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              label="Descripcion"
              placeholder="Ej: Politicas de devolucion, Horarios de atencion"
              fullWidth
              helperText="Un nombre claro que describa esta informacion"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: RADIUS_ITEM,
                  bgcolor: SURFACE_CONTAINER_LOW,
                  fontFamily: 'Jost, sans-serif',
                  '& fieldset': { borderColor: 'transparent' },
                  '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: '1.5px' },
                },
                '& .MuiInputLabel-root': { fontFamily: 'Jost, sans-serif' },
              }}
            />

            <TextField
              value={value}
              onChange={(e) => setValue(e.target.value)}
              label="Contenido"
              placeholder="Describe la informacion que quieres que tu agente conozca..."
              fullWidth
              multiline
              rows={6}
              helperText="Esta informacion estara disponible para tu agente durante las conversaciones"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: RADIUS_ITEM,
                  bgcolor: SURFACE_CONTAINER_LOW,
                  fontFamily: 'Jost, sans-serif',
                  '& fieldset': { borderColor: 'transparent' },
                  '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: '1.5px' },
                },
                '& .MuiInputLabel-root': { fontFamily: 'Jost, sans-serif' },
              }}
            />
          </Stack>

          {/* Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, pt: 1 }}>
            <Button
              onClick={close}
              variant="outlined"
              sx={{
                borderRadius: RADIUS_COIN,
                textTransform: 'none',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                color: NAVY,
                borderColor: `rgba(21, 24, 58, 0.15)`,
                '&:hover': { borderColor: NAVY },
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!isFormValid}
              sx={{
                borderRadius: RADIUS_COIN,
                textTransform: 'none',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                bgcolor: YELLOW,
                color: NAVY,
                '&:hover': { bgcolor: YELLOW, opacity: 0.85 },
                '&.Mui-disabled': { bgcolor: `rgba(21, 24, 58, 0.08)`, color: `rgba(21, 24, 58, 0.3)` },
              }}
            >
              Crear Seccion
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};

export default SimplePromptSectionModal;
